import { useState } from 'react';
import { getKeyHandler } from 't-lib-ui/structure';
import type { RestUserFeed } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import FeedIcon from './FeedIcon';
import ChevronRightIcon from './ChevronRightIcon';

export type FeedUnread = { feed: RestUserFeed; unread: number };

/** Up to this many affected feeds are listed right away; more collapse behind an expander. */
const MAX_INLINE_FEEDS = 1;

const FeedFallbackIcon = () => (
  <svg className="rbkFeedIcon" viewBox="0 0 16 16" width={16} height={16}>
    <rect x="1" y="1" width="14" height="14" rx="3" fill="currentColor" opacity="0.25" />
  </svg>
);

const FeedRows = ({ feeds }: { feeds: FeedUnread[] }) => (
  <ul className="rbkFeeds">
    {feeds.map(({ feed, unread }) => (
      <li key={feed.id} className="rbkFeed">
        <FeedIcon className="rbkFeedIcon" src={getFeedIconUrl(feed, 32)} size={16} circular={feed.circularIcon} fallback={<FeedFallbackIcon />} />
        <span className="rbkFeedName">{feed.shortTitle || feed.title}</span>
        <span className="rbkFeedCount">{unread.toLocaleString()}</span>
      </li>
    ))}
  </ul>
);

/** The feeds an option would affect: listed directly when few, otherwise behind a
 * FlowForm-focusable toggle (Enter/Space expand, arrows move on). */
const AffectedFeeds = ({ feeds }: { feeds: FeedUnread[] }) => {
  const [open, setOpen] = useState(false);
  if (!feeds.length) return null;
  if (feeds.length <= MAX_INLINE_FEEDS) return <FeedRows feeds={feeds} />;
  const toggle = () => setOpen(o => !o);
  return (
    <div className="rbkFeedsBox">
      <button type="button" className={`rbkExpand flowFocussable ${open ? 'open' : ''}`} tabIndex={0} aria-expanded={open}
        onClick={toggle} onKeyDown={getKeyHandler(toggle)}>
        <ChevronRightIcon size={14} />
        {open ? 'Hide' : 'Show'} the {feeds.length} affected feeds
      </button>
      {open && <FeedRows feeds={feeds} />}
    </div>
  );
};

export default AffectedFeeds;
